import { faDeleteLeft, faPen, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, router, InfiniteScroll } from "@inertiajs/react";
import { useRef, useState, useEffect } from "react";
import { CollapsibleSection } from "../Homepage/HomeForm/CollapsibleSection";
import AdminReferenceCard from "../../../components/AdminReferenceCard";
import { handleTableScroll } from "../../../utils/infiniteScroll";
import { debounce } from "../Attachments/RecordsTable";
import { truncateText } from "../../../utils/strings";

const ONLY_PROPS = ['expanded_section_references', 'previewed_reference', 'previewed_references_section', 'references_section_id', 'reference_id'];

// Debounced router visit with 300ms delay
const debounceQueryVisit = debounce((query: string) => {
  router.visit('', {
    data: {
      query
    },
    preserveState: true,
    preserveScroll: true
  })
}, 300);

const navigateToReference = (path: string, searchQuery: string) => {
  const queryParam = searchQuery ? `?query=${encodeURIComponent(searchQuery)}` : '';
  router.visit(`${path}${queryParam}`, {
    preserveState: true,
    preserveScroll: true,
    only: ONLY_PROPS
  });
};

const handleSectionDelete = (referenceSection: any) => {
  if (window.confirm(`Are you sure you want to delete reference section "${referenceSection.title}"?`)) {
    router.delete(`/admin/references_sections/${referenceSection.id}`);
  }
}

function ReferenceSectionCard({ section, expandedSectionReferences, previewedReferenceId, expanded, onToggle, searchQuery }: any) {
  const sectionScrollRef = useRef<any>(null);
  const references = expandedSectionReferences || [];

  return (
    <CollapsibleSection.Container
      defaultExpanded={expanded}
      onToggle={onToggle}
      headerContent={(
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="text-sm font-semibold" style={{ margin: 0 }}>
              {section.title}
              {section.references_count !== undefined && (
                <span style={{ fontWeight: 'normal', opacity: 0.6 }}> ({section.references_count})</span>
              )}
            </span>
            {section.text_preview && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '0.75rem',
                  color: 'var(--color-base-content)',
                  opacity: 0.5,
                }}
              >
                {truncateText(section.text_preview, 50)}
              </span>
            )}
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            flexShrink: 0,
          }}>
            <a
              className="text-xs link link-success"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToReference(`/admin/reference/${section.id}/new`, searchQuery);
              }}
              title="New reference"
            >
              <FontAwesomeIcon icon={faPlus} />
            </a>
            <a
              className="text-xs link link-primary"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToReference(`/admin/reference/${section.id}/edit`, searchQuery);
              }}
              title="Edit section"
            >
              <FontAwesomeIcon icon={faPen} />
            </a>
            <div
              className="text-xs link link-error"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                handleSectionDelete(section);
              }}
              title="Delete section"
            >
              <FontAwesomeIcon icon={faDeleteLeft} />
            </div>
          </div>
        </div>
      )}
    >
      {references && references.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            ref={sectionScrollRef}
            className="hidden-scrollbar"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {
              references.map((reference: any) => (
                <AdminReferenceCard
                  key={`reference-card-${reference.id}`}
                  reference={reference}
                  previewedReferenceId={previewedReferenceId}
                  searchQuery={searchQuery}
                />
              ))
            }
          </div>
          <div
            className="text-xs link link-success"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '4px',
              marginTop: '2px',
              flexShrink: 0,
            }}
            onClick={(e) => {
              e.preventDefault();
              navigateToReference(`/admin/reference/${section.id}/new`, searchQuery);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Nouvelle référence
          </div>
        </div>
      ) : expanded ? (
        <div style={{
          textAlign: 'center',
          padding: '8px',
        }}>
          <div
            className="text-xs link link-success"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
            onClick={(e) => {
              e.preventDefault();
              navigateToReference(`/admin/reference/${section.id}/new`, searchQuery);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Nouvelle référence
          </div>
        </div>
      ) : null}
    </CollapsibleSection.Container>
  );
}

export default function ReferenceList({
  referencesSections,
  expandedSectionReferences,
  expandedSectionId,
  previewedReferenceId,
  searchQuery = ''
}: any) {
  const infiniteScrollRef = useRef<any>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');

  // Handle '/' key press to focus search input and Escape to blur
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Focus search input on '/' key press
      if (e.key === '/' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Blur search input on Escape key press
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div style={{
      backgroundColor: 'white',
      height: '100%',
      border: '2px solid #ddd',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    }}>
      <div style={{
        width: '100%',
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #ddd',
        padding: '4px',
        fontWeight: 'bold',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <div>
          References
          {referencesSections.metadata && (
            <span className="text-sm" style={{ marginLeft: '8px', fontWeight: 'normal' }}>
              {referencesSections.metadata.total} {referencesSections.metadata.total === 1 ? 'section' : 'sections'}
            </span>
          )}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
        }}>
          <Link href="/admin/reference/new">
            <button className="btn btn-sm btn-success btn-primary">
              New Section
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </Link>
        </div>
      </div>
      <div style={{
        padding: '4px',
        backgroundColor: 'white',
        borderBottom: '1px solid #ddd',
      }}>
        <label className="input input-sm">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="search"
            placeholder="Search"
            ref={searchInputRef}
            value={localSearchQuery}
            onChange={(e) => {
              const newQuery = e.target.value;
              setLocalSearchQuery(newQuery);
              debounceQueryVisit(newQuery);
            }}
          />
          <kbd className="kbd kbd-sm">/</kbd>
        </label>
      </div>
      <InfiniteScroll
        ref={infiniteScrollRef}
        data="references_sections"
        manual
        className="hidden-scrollbar"
        style={{
          padding: '2px 4px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}
        onScroll={(e: React.UIEvent<HTMLDivElement>) => handleTableScroll(e, infiniteScrollRef)}
      >
        {referencesSections.data && referencesSections.data.length > 0 ? (
          referencesSections.data.map((section: any) => (
            <ReferenceSectionCard
              key={`reference-section-${section.id}`}
              section={section}
              expandedSectionReferences={expandedSectionId == section.id ? expandedSectionReferences : null}
              previewedReferenceId={previewedReferenceId}
              expanded={expandedSectionId == section.id}
              searchQuery={localSearchQuery}
              onToggle={(willExpand: boolean) => {
                if (willExpand) {
                  navigateToReference(`/admin/reference/${section.id}/edit`, localSearchQuery);
                }
                // Collapse is handled by local state only - no navigation
              }}
            />
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            color: '#999',
          }}>
            No reference sections available
          </div>
        )}
        {referencesSections.metadata?.has_more && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => infiniteScrollRef.current.fetchNext()}
            >
              Load more
            </button>
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
}
